// src/broadcast/providers/BroadcastProvider.tsx
"use client"

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode
} from 'react'
import { useUserStore } from '@/stores/userStore/useUserStore'
import { useUserChannelBroadcast } from '../hooks/useUserChannelBroadcast'
import { useMessageToast } from '../hooks/useMessageToast'
import { useBroadcastStore } from '../stores/useBroadcastStore'

/**
 * Context value type
 */
interface BroadcastContextValue {
  /** Whether connected to broadcast channel */
  isConnected: boolean

  /** Current connection error, if any */
  error: Error | null

  /** Current channel state */
  channelState: string

  /** Send a broadcast message */
  send: (event: string, payload: unknown) => Promise<string>

  /** User ID for the current subscription */
  userId: string | null
}

const BroadcastContext = createContext<BroadcastContextValue | null>(null)

/**
 * Provider props
 */
interface BroadcastProviderProps {
  children: ReactNode
}

/**
 * Broadcast Provider
 * Wraps the application and manages the broadcast channel subscription
 * Must be used within UserStore provider
 */
export function BroadcastProvider({ children }: BroadcastProviderProps) {
  // Get current user from user store
  const user = useUserStore((s) => s.user)
  const userId = user?.id ?? null

  // Reset store when user changes
  const reset = useBroadcastStore((s) => s.reset)

  useEffect(() => {
    // Reset broadcast store when user logs out
    if (!userId) {
      reset()
    }
  }, [userId, reset])

  // Connect to broadcast channel
  const { isConnected, error, channelState, send } = useUserChannelBroadcast({
    userId: userId ?? '',
    enabled: !!userId,
    onError: (err) => {
      console.error('[BroadcastProvider] Error:', err.message)
    },
    onConnectionChange: (connected) => {
      console.log('[BroadcastProvider] Connection changed:', connected)
    },
  })

  // Global toast notifications for new messages
  // Only enabled when user is connected
  useMessageToast({ enabled: !!userId && isConnected })

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<BroadcastContextValue>(() => ({
    isConnected,
    error,
    channelState,
    send,
    userId,
  }), [isConnected, error, channelState, send, userId])

  return (
    <BroadcastContext.Provider value={value}>
      {children}
    </BroadcastContext.Provider>
  )
}

/**
 * Hook to access broadcast context
 * Must be used within BroadcastProvider
 */
export function useBroadcast(): BroadcastContextValue {
  const context = useContext(BroadcastContext)

  if (!context) {
    throw new Error('useBroadcast must be used within BroadcastProvider')
  }

  return context
}

/**
 * Hook to check if broadcast is available (user is logged in)
 */
export function useBroadcastAvailable(): boolean {
  const context = useContext(BroadcastContext)
  return context?.userId !== null
}
